/**
 * FreshCards Card — primary primitive.
 * Shadow + hover transition. Reads theme from --fc-* vars.
 */
import React from 'react';
import { Card as MantineCard, type CardProps as MantineCardProps } from '@mantine/core';

export interface CardProps extends MantineCardProps {
  interactive?: boolean; // adds hover lift + cursor
}

const baseStyle: React.CSSProperties = {
  backgroundColor: 'var(--fc-bg-surface, #ffffff)',
  borderColor: 'var(--fc-border-primary, #e9e9e7)',
  borderRadius: 'var(--fc-radius-lg, 8px)',
  boxShadow: 'var(--fc-shadow-xs, 0 1px 2px rgba(0,0,0,0.04))',
  transition: 'all 150ms ease',
};

const interactiveStyle: React.CSSProperties = {
  cursor: 'pointer',
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ interactive, style, ...rest }, ref) => {
    return (
      <MantineCard
        ref={ref}
        withBorder
        radius="lg"
        padding="md"
        style={{ ...baseStyle, ...(interactive ? interactiveStyle : null), ...style }}
        onMouseEnter={(e) => {
          if (interactive) {
            (e.currentTarget as HTMLElement).style.boxShadow =
              'var(--fc-shadow-sm, 0 2px 4px rgba(0,0,0,0.06))';
            (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
          }
          rest.onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          if (interactive) {
            (e.currentTarget as HTMLElement).style.boxShadow = baseStyle.boxShadow as string;
            (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
          }
          rest.onMouseLeave?.(e);
        }}
        {...rest}
      />
    );
  }
);

Card.displayName = 'Card';
